// Utility types and constants for posts translations
// Category translations for posts
export const CATEGORY_TRANSLATIONS: Record<
  string,
  Record<SupportedLang, string>
> = {
  theory: { ru: 'Теория', en: 'Theory' },
  composers: { ru: 'Композиторы', en: 'Composers' },
  'musical-instruments': { ru: 'Инструменты', en: 'Instruments' },
  'piano-technique': { ru: 'Техника', en: 'Technique' },
  'music-history': { ru: 'История', en: 'History' },
};
export type SupportedLang = 'en' | 'ru';

export const SUPPORTED_LANGUAGES = [
  { name: 'English', slug: 'en', flag_url: '' },
  { name: 'Русский', slug: 'ru', flag_url: '' },
] as const;

// Translation labels for posts pages
export const POSTS_LABELS: Record<
  SupportedLang,
  {
    readMore: string;
    practice: string;
    noVideo: string;
    loadMore: string;
    startLearning: string;
    description: string;
    play: string;
    pagination: {
      previous: string;
      next: string;
      goToPage: string;
      of: string;
    };
    footer: {
      articles: string;
      startLearning: string;
      contactUs: string;
      telegram: string;
      copyright: string;
    };
  }
> = {
  en: {
    readMore: 'Read more',
    practice: 'Practice',
    noVideo: 'Video not available',
    loadMore: 'Load More',
    startLearning: 'Start Learning',
    description:
      'Sheet music, interactive trainer. Connect a MIDI keyboard and play for free.',
    play: 'Start practicing',
    pagination: {
      previous: 'Back',
      next: 'Next',
      goToPage: 'Go to page:',
      of: 'of',
    },
    footer: {
      articles: 'Articles',
      startLearning: 'Start Learning',
      contactUs: 'Contact Us',
      telegram: 'Telegram',
      copyright: '© 2025 DarPiano. All rights reserved.',
    },
  },
  ru: {
    readMore: 'Читать далее',
    practice: 'Играть',
    noVideo: 'Видео недоступно',
    loadMore: 'Показать ещё',
    startLearning: 'Начать обучение',
    description:
      'Ноты, интерактивный тренажер произведения. Подключи MIDI клавиатуру и играй бесплатно.',
    play: 'Начать играть',
    pagination: {
      previous: 'Назад',
      next: 'Вперед',
      goToPage: 'Перейти к странице:',
      of: 'из',
    },
    footer: {
      articles: 'Статьи',
      startLearning: 'Начать обучение',
      contactUs: 'Связаться с нами',
      telegram: 'Telegram',
      copyright: '© 2025 DarPiano. Все права защищены.',
    },
  },
};

// Type for Post object
export interface Post {
  id: number;
  slug: string;
  title: { rendered: string };
  content: { rendered: string };
  excerpt: { rendered: string };
  _embedded?: {
    'wp:featuredmedia'?: Array<{ source_url: string }>;
  };
  acf?: {
    reference?: string;
    thumbnail?: { url: string; ID: number };
    link_to_darpiano_sample?: string;
    youtube?: string;
    translation_group_id?: string;
    lang?: string;
    video_file?: string;
    category_slug?: string; // Added for category translation
  };
  lang?: string;
  date?: string;
  composition_category?: number[];
}

// Helper function to detect language from slug
// Simplified function to get post language from metadata
export function getPostLang(post: Post): SupportedLang | null {
  // Use explicit language field from WordPress metadata
  if (post.acf?.lang) {
    return post.acf.lang as SupportedLang;
  }
  if (post.lang) {
    return post.lang as SupportedLang;
  }
  
  // If no language metadata, return null to indicate unknown language
  return null;
}

// Utility function to get labels for a specific language
export function getPostsLabels(lang: string): typeof POSTS_LABELS.en {
  return POSTS_LABELS[lang as SupportedLang] || POSTS_LABELS.en;
}

// Utility function to group posts by translation group
export function groupPostsByTranslation(posts: Post[]): Record<string, Post[]> {
  const groups: Record<string, Post[]> = {};
  posts.forEach((post) => {
    const groupId = post.acf?.translation_group_id || post.id.toString();
    if (!groups[groupId]) groups[groupId] = [];
    groups[groupId].push(post);
  });
  return groups;
}

// Utility function to build translations map for a group
export function buildTranslationsMap(
  groups: Record<string, Post[]>,
  currentLang: string
): Record<string, string> {
  const currentGroup =
    Object.values(groups).find((group) =>
      group.some((p) => {
        const lang = getPostLang(p);
        return lang && lang === currentLang;
      })
    ) || Object.values(groups)[0];

  const translations: Record<string, string> = {};
  if (currentGroup) {
    currentGroup.forEach((p) => {
      const lang = getPostLang(p);
      if (p.slug && lang) translations[lang] = p.slug; // only add if language is determined
    });
  }

  return translations;
}

// Utility function to get display posts based on current language
// When posts are already filtered by language, we can return them directly
export function getDisplayPosts(
  groups: Record<string, Post[]>,
  currentLang: string
): Post[] {
  // If posts are already language-filtered, return all posts from all groups
  const allPosts = Object.values(groups).flat();
  
  // Filter out posts with undetermined language first
  const postsWithLanguage = allPosts.filter(post => getPostLang(post) !== null);
  
  // Check if posts are already filtered by language
  const allSameLanguage = postsWithLanguage.every(post => getPostLang(post) === currentLang);
  
  if (allSameLanguage && postsWithLanguage.length > 0) {
    // Posts are already filtered, return them all
    return postsWithLanguage;
  }
  
  // Fallback to original logic for mixed-language groups
  return Object.values(groups).map((group) => {
    // Filter out posts with undetermined language
    const validPosts = group.filter((p) => getPostLang(p) !== null);
    const byLang = validPosts.find((p) => getPostLang(p) === currentLang);
    return byLang || validPosts[0];
  }).filter(Boolean); // Remove any undefined results
}

// Utility function to build translations for a single post
export async function buildPostTranslations(
  post: Post,
  apiUrl: string
): Promise<Record<string, string>> {
  const translations: Record<string, string> = {};
  const translationGroupId = post.acf?.translation_group_id;

  if (translationGroupId) {
    try {
      const { default: axios } = await import('axios');
      const groupRes = await axios.get(
        `${apiUrl}/wp-json/wp/v2/music_composition?acf_translation_group_id=${translationGroupId}`
      );
      const groupPosts: Post[] = groupRes.data.filter(
        (p: Post) => p.acf?.translation_group_id === translationGroupId
      );

      groupPosts.forEach((p) => {
        const lang = getPostLang(p);
        if (p.slug && lang) translations[lang] = p.slug; // only add if language is determined
      });
    } catch (error) {
      console.error('Error fetching translation group:', error);
    }
  } else if (post.slug) {
    // If no group, just add the current post
    const lang = getPostLang(post);
    if (lang) translations[lang] = post.slug; // only add if language is determined
  }

  return translations;
}

// Utility function to get the correct post translation for display
export async function getPostForLanguage(
  initialPost: Post,
  currentLang: SupportedLang,
  apiUrl: string
): Promise<Post> {

  const translationGroupId = initialPost.acf?.translation_group_id;

  if (translationGroupId) {
    try {
      const { default: axios } = await import('axios');

      // Fetch all posts instead of using API query parameter

      const allPostsRes = await axios.get(
        `${apiUrl}/wp-json/wp/v2/music_composition?per_page=100`
      );


      // Filter posts by translation group ID
      const groupPosts: Post[] = allPostsRes.data.filter(
        (p: Post) => p.acf?.translation_group_id === translationGroupId
      );


      // Find the post in the current language
      const translatedPost = groupPosts.find(
        (p) => getPostLang(p) === currentLang
      );


      if (translatedPost) {
        return translatedPost;
      } else {
        return initialPost;
      }
    } catch (error) {
      console.error(
        '[GET_POST_FOR_LANG] Error fetching translated post:',
        error
      );
      return initialPost;
    }
  }

  // If no translation group, return the original post
  return initialPost;
}

// --- Category REST API and translation helpers ---

// Get translated category label from slug
export function getTranslatedCategoryLabel(
  slug: string,
  lang: SupportedLang
): string {
  return CATEGORY_TRANSLATIONS[slug]?.[lang] || slug;
}

// Get translated category labels for a post
export function getPostCategoryLabels(
  post: Post,
  categories: Array<{ id: number; slug: string }>,
  lang: SupportedLang
): string[] {
  if (!post.composition_category) return [];
  return post.composition_category
    .map((catId: number) => {
      const cat = categories.find((c) => c.id === catId);
      return cat ? getTranslatedCategoryLabel(cat.slug, lang) : null;
    })
    .filter(Boolean) as string[];
}
